import React, { useState } from 'react';
import styled from 'styled-components';
import IconWrapper from '../common/IconWrapper';
import { FaCogs, FaBell, FaShieldAlt, FaMoon, FaSun, FaLanguage, FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const SettingsContainer = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: #141340;
  margin-bottom: 1rem;
`;

const Form = styled.form`
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Label = styled.label`
  font-weight: 500;
  min-width: 120px;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: #f8f9fa;
`;

const Toggle = styled.input`
  width: 40px;
  height: 20px;
`;

const Button = styled.button`
  background: #141340;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.2s;
  &:hover {
    background: #F68B21;
  }
`;

const SuccessMsg = styled.div`
  color: #28a745;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 2rem;
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const Column = styled.div`
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 2rem 0;
`;

const AdminSettings: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [language] = useState('English');
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    system: true,
  });
  const [twoFA, setTwoFA] = useState(false);
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTheme(e.target.value as 'light' | 'dark');
    setSuccess('Theme updated (mock only).');
  };
  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotifications({ ...notifications, [e.target.name]: e.target.checked });
    setSuccess('Notification preferences updated (mock only).');
  };
  const handle2FAToggle = () => {
    setTwoFA(v => !v);
    setSuccess('Security settings updated (mock only).');
  };
  const handleChangePassword = () => {
    navigate('/admin/profile');
  };

  return (
    <SettingsContainer>
      <SectionTitle><IconWrapper icon={FaCogs} /> Admin Settings</SectionTitle>
      {success && <SuccessMsg>{success}</SuccessMsg>}
      <FlexRow>
        <Column>
          <Form autoComplete="off">
            <SectionTitle><IconWrapper icon={FaSun} /> System Preferences</SectionTitle>
            <FormGroup>
              <Label htmlFor="theme">Theme</Label>
              <Select name="theme" value={theme} onChange={handleThemeChange}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label><IconWrapper icon={FaLanguage} /> Language</Label>
              <span style={{ fontWeight: 500 }}>English</span>
            </FormGroup>
          </Form>
        </Column>
        <Column>
          <Form autoComplete="off">
            <SectionTitle><IconWrapper icon={FaBell} /> Notification Preferences</SectionTitle>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Toggle type="checkbox" name="email" checked={notifications.email} onChange={handleNotificationChange} />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="sms">SMS</Label>
              <Toggle type="checkbox" name="sms" checked={notifications.sms} onChange={handleNotificationChange} />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="system">System Alerts</Label>
              <Toggle type="checkbox" name="system" checked={notifications.system} onChange={handleNotificationChange} />
            </FormGroup>
          </Form>
        </Column>
      </FlexRow>
      <Divider />
      <SectionTitle><IconWrapper icon={FaShieldAlt} /> Security</SectionTitle>
      <Form autoComplete="off" style={{ maxWidth: 400 }}>
        <FormGroup>
          <Label htmlFor="2fa">Two-Factor Auth</Label>
          <Toggle type="checkbox" name="2fa" checked={twoFA} onChange={handle2FAToggle} />
        </FormGroup>
        <FormGroup>
          <Label><IconWrapper icon={FaLock} /> Change Password</Label>
          <Button type="button" onClick={handleChangePassword}>Change Password</Button>
        </FormGroup>
      </Form>
    </SettingsContainer>
  );
};

export default AdminSettings; 